// Entity list page (for Catalogs and Documents)
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useConnection } from '@/app/contexts/ConnectionContext';
import { TokenManager } from '@/utils/api';
import { getSynonymFromRecord } from '@/designer/utils/synonyms';
import { useAppLocale } from '@/app/permissions/useAppLocale';
import { logger } from '@/utils/logger';
import { MicroLabel, OperatorEmptyState, PanelSurface } from '@/shared/operator';

interface Entity {
  name: string;
  type: string;
  table_name: string;
  synonyms?: Record<string, string>;
}

export const EntityListPage: React.FC = () => {
  const { t } = useTranslation();
  const { entityType } = useParams<{ entityType: 'catalogs' | 'documents' }>();
  const navigate = useNavigate();
  const { selectedConnection } = useConnection();
  const { locale, defaultCode } = useAppLocale();
  const [entities, setEntities] = React.useState<Entity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadEntities = async () => {
      if (!selectedConnection) {
        setError(t('connections.selectConnection'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const token = TokenManager.getAccessToken();
        if (!token) {
          setError(t('auth.loginRequired'));
          setLoading(false);
          return;
        }

        const response = await fetch(
          `/api/v1/designer/entities?connection_id=${selectedConnection.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const entityList = entityType === 'catalogs' ? data.catalogs : data.documents;
          setEntities(entityList || []);
        } else {
          setError(t('common.errorLoadingData'));
        }
      } catch (err) {
        logger.error('Error loading entities:', err);
        setError(t('common.errorLoadingData'));
      } finally {
        setLoading(false);
      }
    };

    loadEntities();
  }, [selectedConnection, entityType, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const title = entityType === 'catalogs' ? t('nav.catalogs', 'Catalogs') : t('nav.documents', 'Documents');
  const kicker = entityType === 'catalogs' ? 'Catalogs' : 'Documents';

  return (
    <Box>
      <MicroLabel>{kicker}</MicroLabel>
      <Typography sx={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.3, lineHeight: 1.2, mt: 0.5, mb: 3 }}>
        {title}
      </Typography>

      {entities.length === 0 ? (
        <PanelSurface fill={false} aria-label={`${kicker} empty state`}>
          <OperatorEmptyState kicker={kicker}>
            {t('common.noData', 'No data available').toUpperCase()}
          </OperatorEmptyState>
        </PanelSurface>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {entities.map((entity) => (
            <PanelSurface
              key={entity.table_name}
              fill={false}
              aria-label={getSynonymFromRecord(entity.synonyms, entity.name, locale, defaultCode)}
            >
              <Box
                onClick={() => navigate(`/${entityType}/${entity.table_name}`)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {getSynonymFromRecord(entity.synonyms, entity.name, locale, defaultCode)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('common.tableName', 'Table')}: {entity.table_name}
                </Typography>
              </Box>
            </PanelSurface>
          ))}
        </Box>
      )}
    </Box>
  );
};
